import { useEffect, useState } from 'react';
import { ChevronLeft, Plus, Sparkles, X } from 'lucide-react';
import { useChatStore } from '@/stores/chat-store';
import { AiConversationList } from '@/components/sidebar/chat/ai-conversation-list';
import { AiConversationThread } from '@/components/sidebar/chat/ai-conversation-thread';
import { cn } from '@/lib/utils';

type View = 'list' | 'chat';

/**
 * Floating AI chat surface with a single context-aware header: identity +
 * live status in the list view, back + conversation title in a thread.
 * Rendered inside the AI chat bubble's anchored window.
 */
export function AiChatWindow({ onClose }: { onClose: () => void }): React.JSX.Element {
  const serviceAvailable = useChatStore((s) => s.serviceAvailable);
  const [view, setView] = useState<View>('list');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!loaded) {
      setLoaded(true);
      useChatStore.setState({ serviceAvailable: true });
      useChatStore.getState().loadAiConversations();
    }
  }, [loaded]);

  function openConversation(id: string): void {
    useChatStore.getState().loadAiMessages(id);
    setView('chat');
  }

  async function handleNew(): Promise<void> {
    await useChatStore.getState().createAiConversation();
    setView('chat');
  }

  return (
    <div className="relative flex h-full flex-col overflow-hidden">
      {/* Ambient AI-tinted glow along the top edge */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-ai/[0.08] to-transparent" />

      <WindowHeader
        view={view}
        online={serviceAvailable}
        onBack={() => setView('list')}
        onNew={handleNew}
        onClose={onClose}
      />
      <div className="h-px shrink-0 bg-gradient-to-r from-transparent via-ai/50 to-transparent" />

      {!serviceAvailable ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 p-4 text-center">
          <p className="text-sm text-muted-foreground">VTAK AI is unavailable</p>
          <p className="text-xs text-muted-foreground/60">Database connection required. Check server logs.</p>
        </div>
      ) : view === 'list' ? (
        <AiConversationList onSelect={openConversation} onNew={handleNew} />
      ) : (
        <AiConversationThread />
      )}
    </div>
  );
}

/** Single header row: identity or thread context on the left, actions on the right. */
function WindowHeader({
  view,
  online,
  onBack,
  onNew,
  onClose,
}: {
  view: View;
  online: boolean;
  onBack: () => void;
  onNew: () => void;
  onClose: () => void;
}): React.JSX.Element {
  const conversations = useChatStore((s) => s.aiConversations);
  const activeConversationId = useChatStore((s) => s.activeConversationId);
  const currentTitle =
    conversations.find((c) => c.id === activeConversationId)?.title ?? 'New conversation';

  return (
    <div className="relative flex items-center gap-2.5 px-3 py-2.5">
      {view === 'chat' ? (
        <HeaderIconButton label="Back to conversations" onClick={onBack}>
          <ChevronLeft className="h-4 w-4" />
        </HeaderIconButton>
      ) : (
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-ai/15 ring-1 ring-ai/30">
          <Sparkles className="h-3.5 w-3.5 text-ai" />
        </span>
      )}

      <div className="min-w-0 flex-1">
        {view === 'chat' ? (
          <p className="truncate text-xs font-medium text-foreground">{currentTitle}</p>
        ) : (
          <span className="font-chakra text-sm font-bold uppercase tracking-wider text-foreground">
            VTAK AI
          </span>
        )}
        <span className="flex items-center gap-1">
          <span className={cn('h-1 w-1 rounded-full', online ? 'animate-pulse bg-ai' : 'bg-red-500')} />
          <span className="font-mono text-[9px] tracking-widest text-muted-foreground">
            {online ? 'ONLINE' : 'OFFLINE'}
          </span>
        </span>
      </div>

      <HeaderIconButton label="New conversation" onClick={onNew}>
        <Plus className="h-4 w-4" />
      </HeaderIconButton>
      <HeaderIconButton label="Close AI chat" onClick={onClose}>
        <X className="h-4 w-4" />
      </HeaderIconButton>
    </div>
  );
}

/** Compact ghost icon button used in the window header. */
function HeaderIconButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-echo-border/30 hover:text-foreground"
    >
      {children}
    </button>
  );
}
